/**
 * CreateDocumentPage — Wizard 3.
 *
 * Step 1: type picker (Invoice / Contract / Memo / Other).
 * Step 2: simple form fields specific to the chosen type.
 * Step 3: preview (renders the rendered document body).
 * Step 4: GiOrbit spinner while signing the canonical JSON.
 * Step 5: "Done!" — download proof JSON + render-as-text fallback.
 *
 * PDF rendering is intentionally out of scope for v1 (would require an
 * extra dependency such as pdf-lib or jsPDF). The "Download as Text"
 * button gives the user a printable plain-text version they can paste
 * into any word processor.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Download, FileText, FileSignature, Receipt, Mail, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/Button';
import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { WizardShell } from '@/layouts/WizardShell';
import { useAuthStore } from '@/stores/authStore';
import { useProofsStore, buildProofCertificate, type Proof, type ProofKind } from '@/stores/proofsStore';
import { useCreateProof } from '@/hooks/useProofs';
import { sha256Hex, downloadJson, shortHash } from '@/utils/hashing';
import { buildDocumentPdf, downloadPdf, type PdfSection } from '@/utils/pdf';
import { cn } from '@/utils/cn';

type DocType = 'invoice' | 'contract' | 'memo' | 'other';
type Step = 'type' | 'form' | 'preview' | 'signing' | 'done';

interface InvoiceData {
  from: string; to: string; amount: string; currency: string;
  items: string; date: string; notes: string;
}
interface ContractData {
  parties: string; terms: string; date: string;
}
interface MemoData {
  title: string; subject: string; body: string;
}
interface OtherData {
  title: string; body: string;
}

type FormData = InvoiceData | ContractData | MemoData | OtherData;

export function CreateDocumentPage() {
  const { t, i18n } = useTranslation('wizards');
  const user = useAuthStore((s) => s.user);
  const addProof = useProofsStore((s) => s.add);
  const createProof = useCreateProof();

  const [step, setStep] = useState<Step>('type');
  const [type, setType] = useState<DocType>('invoice');
  const [data, setData] = useState<FormData>(() => emptyFor('invoice'));
  const [proof, setProof] = useState<Proof | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stepNum = step === 'type' ? 1 : step === 'form' ? 2 : step === 'preview' ? 3 : step === 'signing' ? 4 : 5;

  const choose = (next: DocType) => {
    setType(next);
    setData(emptyFor(next));
    setStep('form');
  };

  const handleSign = async () => {
    setError(null);
    setStep('signing');
    try {
      const canonical = JSON.stringify({ type, data, signedBy: user?.email ?? 'anonymous' });
      const hash = await sha256Hex(canonical);
      const name = displayNameFor(type, data);
      const kind: ProofKind = type === 'other' ? 'document' : type;
      const created = addProof({
        kind,
        name,
        hash,
        signer: user?.email ?? 'anonymous',
      });
      // See SignFilePage for the fire-and-forget rationale.
      createProof.mutate(created);
      await new Promise((r) => setTimeout(r, 700));
      setProof(created);
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown_error');
      setStep('preview');
    }
  };

  const reset = () => {
    setProof(null);
    setData(emptyFor(type));
    setStep('type');
  };

  const titleByStep: Record<Step, string> = {
    type:     t('create_document.step_type'),
    form:     t('create_document.step_form'),
    preview:  t('create_document.step_preview'),
    signing:  t('create_document.step_signing'),
    done:     t('create_document.step_done'),
  };

  // exactOptionalPropertyTypes: only attach `onBack` on steps that
  // actually have a meaningful back target.
  const wizardProps =
    step === 'type'
      ? { totalSteps: 5, currentStep: stepNum, title: `${t('create_document.page_title')} — ${titleByStep[step]}` }
      : {
          totalSteps: 5,
          currentStep: stepNum,
          title: `${t('create_document.page_title')} — ${titleByStep[step]}`,
          onBack: () => setStep(step === 'form' ? 'type' : step === 'preview' ? 'form' : 'type'),
        };

  return (
    <WizardShell {...wizardProps}>
      {step === 'type' ? (
        <TypeStep type={type} onChoose={choose} />
      ) : step === 'form' ? (
        <FormStep type={type} data={data} setData={setData} onNext={() => setStep('preview')} />
      ) : step === 'preview' ? (
        <PreviewStep
          type={type}
          data={data}
          locale={i18n.language}
          onSign={handleSign}
          error={error}
        />
      ) : step === 'signing' ? (
        <SigningStep />
      ) : (
        <DoneStep proof={proof} type={type} data={data} onAnother={reset} />
      )}
    </WizardShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// Step components
// ────────────────────────────────────────────────────────────────────

function TypeStep({ type, onChoose }: { type: DocType; onChoose: (t: DocType) => void }) {
  const { t } = useTranslation('wizards');
  const types: { key: DocType; icon: LucideIcon }[] = [
    { key: 'invoice', icon: Receipt },
    { key: 'contract', icon: FileSignature },
    { key: 'memo', icon: Mail },
    { key: 'other', icon: FileText },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {types.map(({ key, icon: Icon }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChoose(key)}
          className={cn(
            'flex flex-col items-start gap-2 rounded-lg border bg-surface p-5 text-start shadow-subtle transition-all',
            'hover:-translate-y-0.5 hover:border-border-strong hover:shadow-raised',
            type === key ? 'border-spark' : 'border-border',
          )}
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-spark-soft text-spark" aria-hidden>
            <Icon className="h-5 w-5" />
          </span>
          <span className="text-base font-semibold">{t(`create_document.type.${key}`)}</span>
        </button>
      ))}
    </div>
  );
}

function FormStep({
  type,
  data,
  setData,
  onNext,
}: {
  type: DocType;
  data: FormData;
  setData: (d: FormData) => void;
  onNext: () => void;
}) {
  const { t } = useTranslation('wizards');

  const set = (key: string, value: string) => {
    // FormData is a discriminated union; spreading + index-update is
    // safe at runtime but TypeScript can't prove the shape stays valid,
    // so we narrow through `unknown`. The caller always picks `key`
    // from the field IDs of the active type, so the shape is preserved.
    const next = { ...(data as unknown as Record<string, string>), [key]: value };
    setData(next as unknown as FormData);
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
    >
      {type === 'invoice' ? (
        <>
          <Field label={t('create_document.fields.from')} id="inv-from" value={(data as InvoiceData).from} onChange={(v) => set('from', v)} />
          <Field label={t('create_document.fields.to')} id="inv-to" value={(data as InvoiceData).to} onChange={(v) => set('to', v)} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('create_document.fields.amount')} id="inv-amount" value={(data as InvoiceData).amount} onChange={(v) => set('amount', v)} />
            <Field label={t('create_document.fields.currency')} id="inv-cur" value={(data as InvoiceData).currency} onChange={(v) => set('currency', v)} />
          </div>
          <Field
            label={t('create_document.fields.items')}
            id="inv-items"
            value={(data as InvoiceData).items}
            onChange={(v) => set('items', v)}
            multiline
            hint={t('create_document.fields.items_hint')}
          />
          <Field type="date" label={t('create_document.fields.date')} id="inv-date" value={(data as InvoiceData).date} onChange={(v) => set('date', v)} />
          <Field label={t('create_document.fields.notes')} id="inv-notes" value={(data as InvoiceData).notes} onChange={(v) => set('notes', v)} multiline />
        </>
      ) : type === 'contract' ? (
        <>
          <Field label={t('create_document.fields.parties')} id="ct-parties" value={(data as ContractData).parties} onChange={(v) => set('parties', v)} multiline />
          <Field label={t('create_document.fields.terms')} id="ct-terms" value={(data as ContractData).terms} onChange={(v) => set('terms', v)} multiline rows={6} />
          <Field type="date" label={t('create_document.fields.date')} id="ct-date" value={(data as ContractData).date} onChange={(v) => set('date', v)} />
        </>
      ) : type === 'memo' ? (
        <>
          <Field label={t('create_document.fields.title')} id="memo-title" value={(data as MemoData).title} onChange={(v) => set('title', v)} />
          <Field label={t('create_document.fields.subject')} id="memo-sub" value={(data as MemoData).subject} onChange={(v) => set('subject', v)} />
          <Field label={t('create_document.fields.body')} id="memo-body" value={(data as MemoData).body} onChange={(v) => set('body', v)} multiline rows={6} />
        </>
      ) : (
        <>
          <Field label={t('create_document.fields.title')} id="other-title" value={(data as OtherData).title} onChange={(v) => set('title', v)} />
          <Field label={t('create_document.fields.body')} id="other-body" value={(data as OtherData).body} onChange={(v) => set('body', v)} multiline rows={8} />
        </>
      )}

      <div className="flex justify-end">
        <Button type="submit" size="lg">
          {t('onboarding.next')}
        </Button>
      </div>
    </form>
  );
}

function PreviewStep({
  type,
  data,
  locale,
  onSign,
  error,
}: {
  type: DocType;
  data: FormData;
  locale: string;
  onSign: () => void;
  error: string | null;
}) {
  const { t } = useTranslation('wizards');
  const text = renderText(type, data, locale);
  return (
    <div className="space-y-4">
      <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-canvas p-4 font-mono text-sm leading-relaxed text-ink whitespace-pre-wrap">
        {text}
      </pre>
      {error ? (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button size="lg" onClick={onSign}>
          {t('create_document.submit')}
        </Button>
      </div>
    </div>
  );
}

function SigningStep() {
  const { t } = useTranslation('wizards');
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      <GiOrbitLogo size="lg" />
      <p className="text-base font-medium">{t('create_document.spinning')}</p>
    </div>
  );
}

function DoneStep({
  proof,
  type,
  data,
  onAnother,
}: {
  proof: Proof | null;
  type: DocType;
  data: FormData;
  onAnother: () => void;
}) {
  const { t, i18n } = useTranslation('wizards');
  const [last, setLast] = useState(proof);
  useEffect(() => { if (proof) setLast(proof); }, [proof]);
  if (!last) return null;

  const kindLabel = t(`create_document.type.${type}`);
  const baseFilename = slug(last.name);
  const [pdfBusy, setPdfBusy] = useState(false);

  const handleDownloadProof = () => {
    downloadJson(`${baseFilename}.proof.json`, buildProofCertificate(last));
  };
  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      const bytes = await buildDocumentPdf({
        kind: kindLabel,
        title: last.name,
        sections: pdfSectionsFor(type, data, t),
        hash: last.hash,
        timestamp: last.timestamp,
        signer: last.signer,
      });
      downloadPdf(`${baseFilename}.pdf`, bytes);
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="h-7 w-7" aria-hidden />
        </span>
        <h2 className="text-xl font-semibold">{t('common.done_title')}</h2>
        <p className="text-sm text-ink-muted">{t('common.done_doc', { kind: kindLabel })}</p>
      </div>

      <dl className="space-y-3 rounded-lg border border-border bg-canvas p-4 text-sm">
        <Row label={t('common.filename')} value={last.name} />
        <Row label={t('common.hash_short')} value={shortHash(last.hash, 12, 12)} mono />
        <Row label={t('common.timestamp')} value={new Date(last.timestamp).toLocaleString(i18n.language)} />
        <Row label={t('common.signer')} value={last.signer} />
      </dl>

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onAnother}>
          {t('common.another_doc')}
        </Button>
        <Button variant="secondary" onClick={handleDownloadProof} leading={<Download className="h-4 w-4" aria-hidden />}>
          {t('common.download_proof')}
        </Button>
        <Button onClick={handleDownloadPdf} disabled={pdfBusy} leading={<Download className="h-4 w-4" aria-hidden />}>
          {t('common.download_pdf')}
        </Button>
      </div>
    </div>
  );
}

// Build the structured section list pdf.ts will render. Keys come from
// the wizards namespace so the rendered PDF is i18n-aware.
function pdfSectionsFor(
  type: DocType,
  data: FormData,
  t: (key: string) => string,
): PdfSection[] {
  const f = (k: string) => t(`create_document.fields.${k}`);
  const lines: PdfSection[] = [];
  if (type === 'invoice') {
    const inv = data as InvoiceData;
    lines.push({ heading: f('date'), lines: [inv.date || '—'] });
    lines.push({ heading: f('from'), lines: splitNonEmpty(inv.from) });
    lines.push({ heading: f('to'),   lines: splitNonEmpty(inv.to) });
    lines.push({ heading: f('items'), lines: splitNonEmpty(inv.items) });
    lines.push({ heading: f('amount'), lines: [`${inv.amount} ${inv.currency}`.trim()] });
    if (inv.notes.trim()) lines.push({ heading: f('notes'), lines: splitNonEmpty(inv.notes) });
  } else if (type === 'contract') {
    const ct = data as ContractData;
    lines.push({ heading: f('date'),   lines: [ct.date || '—'] });
    lines.push({ heading: f('parties'), lines: splitNonEmpty(ct.parties) });
    lines.push({ heading: f('terms'),   lines: splitNonEmpty(ct.terms) });
  } else if (type === 'memo') {
    const m = data as MemoData;
    lines.push({ heading: f('subject'), lines: [m.subject || '—'] });
    lines.push({ heading: f('body'),    lines: splitNonEmpty(m.body) });
  } else {
    const o = data as OtherData;
    lines.push({ heading: f('title'), lines: [o.title || '—'] });
    lines.push({ heading: f('body'),  lines: splitNonEmpty(o.body) });
  }
  return lines;
}

function splitNonEmpty(s: string): string[] {
  return s
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

function emptyFor(t: DocType): FormData {
  const today = new Date().toISOString().slice(0, 10);
  if (t === 'invoice') return { from: '', to: '', amount: '', currency: 'USD', items: '', date: today, notes: '' };
  if (t === 'contract') return { parties: '', terms: '', date: today };
  if (t === 'memo') return { title: '', subject: '', body: '' };
  return { title: '', body: '' };
}

function displayNameFor(type: DocType, data: FormData): string {
  if (type === 'invoice') {
    const inv = data as InvoiceData;
    const to = inv.to.trim() || 'Recipient';
    const amount = inv.amount.trim() ? `${inv.amount} ${inv.currency || ''}`.trim() : '';
    return amount ? `Invoice — ${to} — ${amount}` : `Invoice — ${to}`;
  }
  if (type === 'contract') {
    const ct = data as ContractData;
    const parties = ct.parties.split('\n')[0]?.slice(0, 60) || 'Contract';
    return `Contract — ${parties}`;
  }
  if (type === 'memo') {
    const m = data as MemoData;
    return m.title.trim() || m.subject.trim() || 'Memo';
  }
  return (data as OtherData).title.trim() || 'Document';
}

function renderText(type: DocType, data: FormData, locale: string): string {
  const today = new Date().toLocaleDateString(locale);
  if (type === 'invoice') {
    const inv = data as InvoiceData;
    return [
      'INVOICE',
      `Date: ${inv.date || today}`,
      `From: ${inv.from}`,
      `To: ${inv.to}`,
      '',
      'Items:',
      inv.items || '(none)',
      '',
      `Amount: ${inv.amount} ${inv.currency}`,
      '',
      inv.notes ? `Notes: ${inv.notes}` : '',
    ].filter(Boolean).join('\n');
  }
  if (type === 'contract') {
    const ct = data as ContractData;
    return [
      'CONTRACT',
      `Date: ${ct.date || today}`,
      '',
      'Parties:',
      ct.parties,
      '',
      'Terms:',
      ct.terms,
    ].join('\n');
  }
  if (type === 'memo') {
    const m = data as MemoData;
    return [
      `MEMO — ${m.title}`,
      `Subject: ${m.subject}`,
      `Date: ${today}`,
      '',
      m.body,
    ].join('\n');
  }
  const o = data as OtherData;
  return [o.title, '', o.body].join('\n');
}

function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9-_]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64) || 'document';
}

// ────────────────────────────────────────────────────────────────────
// Atoms
// ────────────────────────────────────────────────────────────────────

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  multiline,
  rows,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'date';
  multiline?: boolean;
  rows?: number;
  hint?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink">
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          rows={rows ?? 3}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-spark focus:outline-none"
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-spark focus:outline-none"
        />
      )}
      {hint ? <p className="mt-1 text-xs text-ink-subtle">{hint}</p> : null}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
      <dt className="text-xs uppercase tracking-wider text-ink-subtle">{label}</dt>
      <dd className={cn('break-all text-sm text-ink', mono && 'font-mono')}>{value}</dd>
    </div>
  );
}
